import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, FilePlus, Pencil, FolderOpen, Clock, BarChart3, ExternalLink } from "lucide-react";
import { useAdminHelpArticles, useHelpCategories } from "@/hooks/useHelpCenter";
import { formatDistanceToNow } from "date-fns";

export function HelpArticlesWidget() {
  const navigate = useNavigate();
  const { data: articles, isLoading: articlesLoading } = useAdminHelpArticles();
  const { data: categories, isLoading: categoriesLoading } = useHelpCategories();

  const isLoading = articlesLoading || categoriesLoading;

  // Calculate stats
  const totalArticles = articles?.length || 0;
  const publishedCount = articles?.filter(a => a.is_published && !a.is_archived).length || 0;
  const draftCount = articles?.filter(a => !a.is_published && !a.is_archived).length || 0;

  // Get last 3 updated articles (non-archived)
  const recentArticles = articles
    ?.filter(a => !a.is_archived)
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 3) || [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Help Articles</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="text-lg">Help Articles</CardTitle>
              <CardDescription className="text-xs">
                {categories?.length || 0} categories
              </CardDescription>
            </div>
          </div>
          <Button 
            size="sm" 
            onClick={() => navigate("/admin/help/articles")}
            variant="outline"
            className="gap-1"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Manage
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div 
            className="bg-muted/50 rounded-lg p-3 text-center cursor-pointer hover:bg-muted transition-colors"
            onClick={() => navigate("/admin/help/articles")}
          >
            <p className="text-2xl font-bold text-primary">{totalArticles}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div 
            className="bg-muted/50 rounded-lg p-3 text-center cursor-pointer hover:bg-muted transition-colors"
            onClick={() => navigate("/admin/help/articles?status=published")}
          >
            <p className="text-2xl font-bold text-accent">{publishedCount}</p>
            <p className="text-xs text-muted-foreground">Published</p>
          </div>
          <div 
            className="bg-muted/50 rounded-lg p-3 text-center cursor-pointer hover:bg-muted transition-colors"
            onClick={() => navigate("/admin/help/articles?status=draft")}
          >
            <p className="text-2xl font-bold text-warning">{draftCount}</p>
            <p className="text-xs text-muted-foreground">Drafts</p>
          </div>
        </div>

        {/* Recent Articles */}
        {recentArticles.length > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Clock className="h-4 w-4" />
              Recently Updated
            </div>
            <div className="space-y-2">
              {recentArticles.map((article) => (
                <div
                  key={article.id}
                  className="flex items-center justify-between p-2 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors group"
                  onClick={() => navigate(`/admin/help/articles/${article.id}/edit`)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                      {article.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge 
                        variant={article.is_published ? "default" : "secondary"} 
                        className="text-[10px] px-1.5 py-0"
                      >
                        {article.is_published ? "Published" : "Draft"}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(article.updated_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/help/articles/${article.id}/edit`);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-6 border border-dashed rounded-lg">
            <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-3">No help articles yet</p>
            <Button
              size="sm"
              onClick={() => navigate("/admin/help/articles/new")}
            >
              <FilePlus className="h-4 w-4 mr-1" />
              Create Your First Article
            </Button>
          </div>
        )}

        {/* Quick Actions */}
        {recentArticles.length > 0 && (
          <>
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button
                size="sm"
                onClick={() => navigate("/admin/help/articles/new")}
                className="flex-1"
              >
                <FilePlus className="h-4 w-4 mr-1" />
                New Article
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/admin/help/articles")}
                className="flex-1"
              >
                <FolderOpen className="h-4 w-4 mr-1" />
                All Articles
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate("/admin/help/categories")}
                className="flex-1 text-muted-foreground hover:text-foreground"
              >
                Categories
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate("/admin/help/analytics")}
                className="flex-1 text-muted-foreground hover:text-foreground"
              >
                <BarChart3 className="h-4 w-4 mr-1" />
                Analytics
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
